'use client';
import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const sections = [
  {
    title: 'Acceptance of Terms',
    content: [
      {
        subtitle: 'Agreement to Terms',
        text: 'By booking a service, joining our waitlist, submitting a quote request, or otherwise using the Vertex Premium Mobile Detailing website or services, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.',
      },
      {
        subtitle: 'Changes to Terms',
        text: 'We reserve the right to modify these Terms and Conditions at any time. Changes will be effective upon posting to our website. Your continued use of our services after any changes constitutes your acceptance of the updated terms.',
      },
    ],
  },
  {
    title: 'Services',
    content: [
      {
        subtitle: 'Service Description',
        text: 'Vertex Premium Mobile Detailing provides professional mobile vehicle detailing services in Mesquite, Nevada and surrounding areas. Services include but are not limited to interior cleaning, exterior washing, waxing, clay bar treatment, steam cleaning, and related detailing work as described in our service packages.',
      },
      {
        subtitle: 'Service Area',
        text: 'Our services are currently available in Mesquite, NV and nearby areas. We reserve the right to decline service requests outside our service area or in locations we determine to be unsafe or inaccessible.',
      },
      {
        subtitle: 'Service Requirements',
        text: 'You are responsible for ensuring that a water spigot (outdoor hose bib) is accessible at the service location. Failure to provide water access may result in rescheduling or cancellation of your appointment. You must also ensure the vehicle is accessible and that the service area is reasonably clear.',
      },
      {
        subtitle: 'Results & Expectations',
        text: 'While we strive to deliver exceptional results, the outcome of detailing services can vary based on the condition of your vehicle, the age of paint, pre-existing damage, stains, or other factors outside our control. We will communicate any concerns before beginning work. We do not guarantee the removal of all stains, scratches, swirl marks, or other pre-existing damage.',
      },
    ],
  },
  {
    title: 'Booking & Scheduling',
    content: [
      {
        subtitle: 'Waitlist & Booking Process',
        text: 'Bookings are accepted through our waitlist system. Joining the waitlist does not guarantee a specific appointment time. We will contact you to confirm availability and schedule your service. Appointment slots are confirmed on a first-come, first-served basis.',
      },
      {
        subtitle: 'Cancellations & Rescheduling',
        text: 'We understand that schedules change. If you need to cancel or reschedule, please contact us as soon as possible — ideally at least 24 hours before your scheduled appointment. Repeated last-minute cancellations may result in removal from our waitlist.',
      },
      {
        subtitle: 'Our Right to Cancel',
        text: 'We reserve the right to cancel or reschedule appointments due to weather conditions, equipment issues, illness, or other circumstances beyond our control. In such cases, we will notify you promptly and work to reschedule at your earliest convenience.',
      },
      {
        subtitle: 'Weather Policy',
        text: 'Exterior detailing services may be affected by rain, extreme heat, or other adverse weather conditions. If weather prevents us from completing your service safely or effectively, we will reschedule at no additional charge.',
      },
    ],
  },
  {
    title: 'Pricing & Payment',
    content: [
      {
        subtitle: 'Pricing',
        text: 'Prices are listed on our website and are subject to change without notice. Final pricing may vary based on vehicle size, condition, and specific service requirements. We will provide a confirmed price before beginning any work.',
      },
      {
        subtitle: 'Payment Terms',
        text: 'Payment is due upon completion of services unless otherwise agreed in writing. We accept cash, Venmo, Zelle, and other payment methods as communicated at the time of booking. Failure to pay may result in legal action and/or collection efforts.',
      },
      {
        subtitle: 'Quotes',
        text: 'Photo quotes submitted through our website are estimates only and are not binding. Final pricing is confirmed after we assess the vehicle in person. We will not begin work without your approval of the final price.',
      },
    ],
  },
  {
    title: 'Your Responsibilities',
    content: [
      {
        subtitle: 'Vehicle Condition',
        text: 'You are responsible for disclosing any known issues with your vehicle that may affect our ability to perform services safely — including but not limited to fragile trim, damaged seals, electrical issues, or pre-existing damage. We are not liable for damage to pre-existing conditions that were not disclosed.',
      },
      {
        subtitle: 'Personal Property',
        text: 'Please remove all valuables, personal items, and important documents from your vehicle before your appointment. Vertex Premium Mobile Detailing is not responsible for any lost, damaged, or stolen personal property left in the vehicle during service.',
      },
      {
        subtitle: 'Accurate Information',
        text: 'You agree to provide accurate and complete information when booking services, including your correct contact information, vehicle details, and service location. Providing false or misleading information may result in cancellation of your service.',
      },
    ],
  },
  {
    title: 'Liability & Disclaimers',
    content: [
      {
        subtitle: 'Limitation of Liability',
        text: "To the fullest extent permitted by law, Vertex Premium Mobile Detailing's total liability to you for any claims arising from our services shall not exceed the amount you paid for the specific service giving rise to the claim. We are not liable for any indirect, incidental, special, consequential, or punitive damages.",
      },
      {
        subtitle: 'Pre-Existing Damage',
        text: 'We are not responsible for damage that existed prior to our service. We will document the condition of your vehicle before beginning work and will notify you of any pre-existing damage we observe. You agree that our documentation represents an accurate record of the vehicle\'s pre-service condition.',
      },
      {
        subtitle: 'Disclaimer of Warranties',
        text: 'Our services are provided "as is" without warranties of any kind, express or implied, except as required by applicable law. We do not warrant that our services will meet your specific expectations or that results will be permanent.',
      },
      {
        subtitle: 'Indemnification',
        text: 'You agree to indemnify and hold harmless Vertex Premium Mobile Detailing, its owner, and employees from any claims, damages, losses, or expenses (including legal fees) arising from your use of our services, your breach of these terms, or your violation of any applicable law.',
      },
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      {
        subtitle: 'Website Content',
        text: 'All content on our website — including text, images, logos, graphics, and design — is the property of Vertex Premium Mobile Detailing and is protected by applicable copyright and intellectual property laws. You may not reproduce, distribute, or use our content without prior written permission.',
      },
      {
        subtitle: 'Photos & Reviews',
        text: 'By submitting photos of your vehicle for a quote or allowing us to photograph your vehicle during service, you grant Vertex Premium Mobile Detailing a non-exclusive, royalty-free license to use those images for marketing and promotional purposes, including on our website and social media. We will not identify you personally without your consent.',
      },
    ],
  },
  {
    title: 'Governing Law',
    content: [
      {
        subtitle: 'Jurisdiction',
        text: 'These Terms and Conditions are governed by and construed in accordance with the laws of the State of Nevada, without regard to its conflict of law provisions. Any disputes arising from these terms or our services shall be resolved in the courts of Clark County or Lincoln County, Nevada.',
      },
      {
        subtitle: 'Dispute Resolution',
        text: 'Before initiating any legal action, you agree to contact us directly to attempt to resolve any dispute informally. We are committed to working with our customers to resolve issues fairly and promptly.',
      },
    ],
  },
  {
    title: 'Miscellaneous',
    content: [
      {
        subtitle: 'Entire Agreement',
        text: 'These Terms and Conditions, together with our Privacy Policy, constitute the entire agreement between you and Vertex Premium Mobile Detailing regarding your use of our services and supersede all prior agreements and understandings.',
      },
      {
        subtitle: 'Severability',
        text: 'If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the remaining terms remain in full force and effect.',
      },
      {
        subtitle: 'No Waiver',
        text: 'Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.',
      },
    ],
  },
  {
    title: 'Contact Us',
    content: [
      {
        subtitle: 'Questions About These Terms',
        text: 'If you have any questions about these Terms and Conditions, please contact us:',
      },
    ],
    contact: true,
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0f' }}>
      <Header />
      <main className="flex-1 relative">
        {/* Ambient background */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(45,95,154,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-20">
          {/* Back link */}
          <Link
            href="/home"
            className="inline-flex items-center gap-2 mono text-[10px] tracking-widest uppercase mb-10 transition-colors duration-200"
            style={{ color: 'rgba(168,169,173,0.5)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#4A7EC0')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(168,169,173,0.5)')}
          >
            <span>←</span>
            <span>Back to Home</span>
          </Link>

          {/* Header */}
          <div className="mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="mono text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ background: 'rgba(45,95,154,0.12)', border: '1px solid rgba(74,126,192,0.2)', color: '#4A7EC0' }}>
                Legal
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3" style={{ color: '#F5F5F7' }}>
              Terms & Condition<span style={{ color: '#4A7EC0' }}>s</span>
            </h1>
            <p className="mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.4)' }}>
              Last Updated: August 2026
            </p>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: 'rgba(168,169,173,0.7)' }}>
              These Terms and Conditions govern your use of Vertex Premium Mobile Detailing's website and services. Please read them carefully before booking or using our services. These terms create a legally binding agreement between you and Vertex Premium Mobile Detailing.
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-px mb-12" style={{ background: 'linear-gradient(90deg, transparent, rgba(74,126,192,0.3), transparent)' }} />

          {/* Sections */}
          <div className="space-y-10 md:space-y-14">
            {sections?.map((section, i) => (
              <div key={i}>
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="mono text-[9px] font-bold tracking-widest w-6 h-6 rounded flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(45,95,154,0.15)', border: '1px solid rgba(74,126,192,0.2)', color: '#4A7EC0' }}
                  >
                    {i + 1}
                  </span>
                  <h2 className="text-base md:text-lg font-semibold tracking-tight" style={{ color: '#F5F5F7' }}>
                    {section?.title}
                  </h2>
                </div>

                <div className="pl-9 space-y-5">
                  {section?.content?.map((item, j) => (
                    <div key={j}>
                      <h3 className="mono text-[10px] font-semibold tracking-widest uppercase mb-2" style={{ color: '#4A7EC0' }}>
                        {item?.subtitle}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(168,169,173,0.75)' }}>
                        {item?.text}
                      </p>
                    </div>
                  ))}

                  {section?.contact && (
                    <div
                      className="mt-4 p-4 rounded-xl space-y-2"
                      style={{ background: 'rgba(45,95,154,0.07)', border: '1px solid rgba(74,126,192,0.15)' }}
                    >
                      <p className="mono text-[10px] font-semibold tracking-widest uppercase mb-3" style={{ color: 'rgba(168,169,173,0.5)' }}>
                        Vertex Premium Mobile Detailing
                      </p>
                      <div className="space-y-2">
                        <a
                          href="tel:+17252711448"
                          className="flex items-center gap-2 text-sm transition-colors duration-200"
                          style={{ color: '#4A7EC0' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#F5F5F7')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                        >
                          <span className="mono text-[10px]">☎</span>
                          725-271-1448
                        </a>
                        <a
                          href="mailto:envizionary.studios@gmail.com"
                          className="flex items-center gap-2 text-sm transition-colors duration-200"
                          style={{ color: '#4A7EC0' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#F5F5F7')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                        >
                          <span className="mono text-[10px]">✉</span>
                          envizionary.studios@gmail.com
                        </a>
                        <a
                          href="https://www.instagram.com/vertexpremiummobiledetailing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm transition-colors duration-200"
                          style={{ color: '#4A7EC0' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#F5F5F7')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                        >
                          <span className="mono text-[10px]">◈</span>
                          @vertexpremiummobiledetailing
                        </a>
                        <p className="text-xs" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          Mesquite, Nevada
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {i < sections?.length - 1 && (
                  <div className="mt-10 md:mt-14 w-full h-px" style={{ background: 'rgba(168,169,173,0.07)' }} />
                )}
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-16 pt-8" style={{ borderTop: '1px solid rgba(168,169,173,0.07)' }}>
            <p className="mono text-[10px] tracking-wide text-center" style={{ color: 'rgba(168,169,173,0.3)' }}>
              These terms apply to all Vertex Premium Mobile Detailing services in Mesquite, Nevada and surrounding areas.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Link
                href="/privacy-policy"
                className="mono text-[10px] tracking-widest uppercase transition-colors duration-200"
                style={{ color: 'rgba(74,126,192,0.6)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(74,126,192,0.6)')}
              >
                ← Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
